import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import * as amplitude from '@amplitude/analytics-browser';
import {
  AnimatedSpinner,
  FilePDF,
  FileSpreadSheet,
} from '@unified-checkout/design-system/icons/index';
import { DataTable } from '@unified-checkout/design-system';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { useLangStore } from '@app/dashboard/store/lang';
import {
  getStatementsOrDigestList,
  downloadStatementsOrDigest,
} from '@app/dashboard/services/inner_layout/reports';
import type {
  TReportsHeaderDateRanges,
  TStatement,
  TStatementFileType,
} from '@app/dashboard/types/innerLayout/reports';
import { IColumnsMap } from 'libs/design-system/src/types/data_table';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import {
  SYSTEM_EVENTS_GROUPING_CODES,
  SYSTEM_EVENTS_SRC_FEATURES
} from '@app/dashboard/constants/tracking_events/system';
import { onToasterShowHandler } from '@app/dashboard/utils/tracking/onToasterShowHandler';
import { downloadFile } from '@app/dashboard/utils/downloadFile';
import { notify } from '@app/dashboard/utils/notifier';
import { extractDownloadPath } from '../utils/extractDownloadPath';
import {
  REPORTS_STATEMENTS_EVENTS,
  REPORTS_STATEMENTS_EVENTS_PARAMS,
  REPORTS_STATEMENTS_EVENTS_VALUES,
} from '@app/dashboard/constants/tracking_events/reports-statements';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import styles from './statementsTab.module.scss';

export const StatementsTab = () => {
  const { t } = useTranslation();
  const { lang } = useLangStore();
  const { isLive } = useTestLiveModeStore();
  const screenWidth = useScreenWidth();

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const [downloading, setDownloading] = useState<{
    id: number;
    type: TStatementFileType
  } | null>(null);

  const downloadMutation = useMutation({
    mutationFn: ({
      statement,
      type,
    }: {
      statement: TStatement;
      type: TStatementFileType
    }) => {
      const fullUrl = type === "pdf" ? statement.pdf_url : statement.excel_url;
      const downloadPath = extractDownloadPath(fullUrl)
      return downloadStatementsOrDigest({ downloadPath });
    },
    onSuccess: (res, variables) => {
      const { type, statement } = variables;
      const format = type === 'pdf'
        ? REPORTS_STATEMENTS_EVENTS_VALUES.FILE_FORMAT.PDF
        : REPORTS_STATEMENTS_EVENTS_VALUES.FILE_FORMAT.XLSX;
      amplitude.track(REPORTS_STATEMENTS_EVENTS.REPORT_DOWNLOAD_SUCCEEDED, {
        [REPORTS_STATEMENTS_EVENTS_PARAMS.REPORT_TYPE]:
          REPORTS_STATEMENTS_EVENTS_VALUES.REPORT_TYPE.STATEMENT,
        [REPORTS_STATEMENTS_EVENTS_PARAMS.FORMAT]: format,
        [SHARED_EVENTS_PARAMS.DATE_RANGE]: statement.title,
      });
      downloadFile({ res, type, defaultFilename: 'statement' });
      setDownloading(null);
    },
    onError: (error: any) => {
      const errorDescription =
        (error?.response?.data?.message) ||
        error?.message ||
        t('errors.somethingWentWrong');
      amplitude.track(REPORTS_STATEMENTS_EVENTS.REPORT_DOWNLOAD_FAILED, {
        [REPORTS_STATEMENTS_EVENTS_PARAMS.REPORT_TYPE]:
          REPORTS_STATEMENTS_EVENTS_VALUES.REPORT_TYPE.STATEMENT,
        [REPORTS_STATEMENTS_EVENTS_PARAMS.ERROR_DESCRIPTION]: errorDescription,
      });
      setDownloading(null);
      notify.error(errorDescription, {
        timeout: 2,
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'error',
            trackingCode: SYSTEM_EVENTS_GROUPING_CODES.REPORTS.STATEMENTS_TAB.DOWNLOAD_STATEMENTS,
            msg: errorDescription,
            srcFeature: SYSTEM_EVENTS_SRC_FEATURES.REPORTS.STATEMENTS_TAB
          })
      });
    },
  });

  const handleDownload = (
    statement: TStatement,
    type: TStatementFileType
  ) => {
    setDownloading({ id: statement.id, type });
    downloadMutation.mutate({ statement, type });
  };

  const columnsMap = [
    {
      id: 'date',
      width: '90%',
      name: t('innerLayout.reportsPage.statementsTable.headers.statementDate'),
      type: {
        conditionsType: 'direct_value',
        conditions: {
          leading_text: { is_new: true },
        },
        default: 'text',
      },
      key: 'title',
      badgeProps: (_row: TStatement, rowIndex: number) => {
        return rowIndex === 0
          ? {
              color: 'green',
              text: t('innerLayout.reportsPage.statementsTable.badges.new'),
              size: 'sm',
            }
          : undefined;
      },
    },
    {
      id: 'actions',
      name: '',
      type: 'action_btns',
      actionBtnsProps: (row: TStatement) => [
        {
          variant: 'outlined',
          text: t('innerLayout.reportsPage.statementsTable.headers.pdf'),
          leadingIcon:
            downloadMutation.isPending && downloading?.id === row.id && downloading?.type === 'pdf'
              ? AnimatedSpinner
              : FilePDF,
          disabled: !row.is_active || (
            downloadMutation.isPending && downloading
          ),
          color: 'secondary',
          onClickHandler: () => handleDownload(row, 'pdf'),
        },
        {
          variant: 'outlined',
          text: t('innerLayout.reportsPage.statementsTable.headers.excel'),
          leadingIcon:
            downloadMutation.isPending && downloading?.id === row.id && downloading?.type === 'xlsx'
              ? AnimatedSpinner
              : FileSpreadSheet,
          disabled: !row.is_active || (
            downloadMutation.isPending && downloading
          ),
          color: 'secondary',
          onClickHandler: () => handleDownload(row, 'xlsx'),
        },
      ],
    },
  ];

  const queryKeys = useMemo(
    () => [QUERY_KEYS.GET_STATEMENTS, isLive],
    [isLive]
  );
 
  const queryFunctionParams = useMemo(
    () => ({ is_live: isLive, platform: 'dashboard', service: 'Statements'}),
    [isLive]
  );

  return (
    <div className={styles['statements-tab-wrapper']}>
      <DataTable
        lang={lang}
        tableBody={{
          lang: lang,
          tableId: 'statements_table',
          dataLocation: 'data.data',
          columnsMap: columnsMap as IColumnsMap,
        }}
        queries={{
          queryKeys: queryKeys,
          queryFunction: getStatementsOrDigestList,
          queryFunctionParams: queryFunctionParams,
        }}
        pagination={{
          type: 'cursor_based',
          dataLocation: {
            next: 'next',
            prev: 'previous',
          },
          hideLabels: isMobile,
        }}
        emptyState={{
          text: t(
            'innerLayout.reportsPage.statementsTable.body.noStatementsYet'
          ),
          description: t(
            'innerLayout.reportsPage.statementsTable.body.noStatementsHint'
          ),
        }}
      />
    </div>
  );
};
